import React, { useState, useEffect } from "react";
import { db } from "../firebase/fire";
import { collection, getDocs, updateDoc, doc, addDoc } from "firebase/firestore";
import LoadingOverlay from "./LoadingOverlay";
import Modal from "./Modal";
import { FaUndo } from "react-icons/fa";

export default function ArchivedCoachesPage({ onRestore, theme = 'light' }) {
    const [archivedCoaches, setArchivedCoaches] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const coachesPerPage = 7;

    const showModal = (message) => {
        setModalMessage(message);
        setModalOpen(true);
    };

    useEffect(() => {
        const fetchArchivedCoaches = async () => {
            setIsLoading(true);
            const snapshot = await getDocs(collection(db, "coaches"));
            const data = snapshot.docs
                .map((doc) => {
                    const d = doc.data();
                    return d ? { id: doc.id, ...d } : null;
                })
                .filter((c) => c && c.archived);
            setArchivedCoaches(data);
            setIsLoading(false);
        };
        fetchArchivedCoaches();
    }, []);

    const logCoachChange = async (coachId, changesObj, actionType) => {
        const changeEntries = Object.entries(changesObj).map(([field, { before, after }]) => ({
            field,
            before,
            after
        }));
        await addDoc(collection(db, "coachAuditLog"), {
            coachId,
            changes: changeEntries,
            action: actionType,
            timestamp: new Date()
        });
    };

    const handleUnarchive = async (coachId) => {
        setIsLoading(true);
        await updateDoc(doc(db, "coaches", coachId), { archived: false });
        const prevCoach = archivedCoaches.find(c => c.id === coachId);
        await logCoachChange(
            coachId,
            { archived: { before: prevCoach?.archived ?? true, after: false } },
            "Restore from Archive"
        );
        setArchivedCoaches((prev) => prev.filter((c) => c.id !== coachId));
        setIsLoading(false);
        showModal("Instructor restored successfully");
        if (typeof onRestore === 'function') {
            try { await onRestore(); } catch (err) { console.error('onRestore error', err); }
        }
    };

    const filteredCoaches = archivedCoaches.filter(
        coach =>
            (coach.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                coach.email?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Pagination logic
    const indexOfLast = currentPage * coachesPerPage;
    const indexOfFirst = indexOfLast - coachesPerPage;
    const currentCoaches = filteredCoaches.slice(indexOfFirst, indexOfLast);

    return (
        <LoadingOverlay isLoading={isLoading}>
            <div className={`container mx-auto p-4 ${
                theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
            }`}>
                
                {/* <h1 className="text-2xl font-bold mb-4">Archived Instructors</h1> */}
                <input
                    type="text"
                    placeholder="Search instructors..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`w-full p-3 mb-4 border-2 rounded-md shadow-sm focus:outline-none focus:ring-2 ${
                        theme === 'dark'
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'
                            : 'bg-white border-blue-500 text-gray-900 focus:ring-blue-300'
                    }`}
                />
                <table className={`w-full text-m text-center ${
                    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}>
                    <thead className={theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-blue-600 text-white'}>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Is Instructor</th>
                            <th>Is Active Coach</th>
                            <th>Barcode</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody className={theme === 'dark' ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-900'}>
                        {currentCoaches.map((coach) => (
                            <tr key={coach.id} className={theme === 'dark' ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                                <td>{coach.name}</td>
                                <td>{coach.email}</td>
                                <td>{coach.phone}</td>
                                <td>{coach.isCoach ? 'Yes' : 'No'}</td>
                                <td>{coach.isActiveCoach ? 'Yes' : 'No'}</td>
                                <td>{coach.barcode || 'N/A'}</td>
                                <td>
                                    <button
                                        onClick={() => handleUnarchive(coach.id)}
                                        className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 flex items-center gap-2 mx-auto"
                                    >
                                        <FaUndo />
                                        Restore
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Pagination Controls */}
                <div className="mt-4 flex justify-center">
                    {Array.from(
                        { length: Math.ceil(archivedCoaches.length / coachesPerPage) },
                        (_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`mx-1 px-3 py-1 rounded ${
                                    currentPage === i + 1
                                        ? 'bg-blue-500 text-white'
                                        : theme === 'dark'
                                            ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                                            : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                                }`}
                            >
                                {i + 1}
                            </button>
                        )
                    )}
                </div>
            </div>
            <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
                <p>{modalMessage}</p>
            </Modal>
        </LoadingOverlay>
    );
}
